import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../../context/auth-context';
import { Button } from '../../components/ui/button';
import { useToast } from '../../components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Loader2, RefreshCw, Package, Ghost, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useOnboardingTracking } from '@/hooks/useOnboardingTracking';

// Import our custom components
import { AgentCard } from '@/components/agents/AgentCard';
import { AgentFilters } from '@/components/agents/AgentFilters';
import { AgentProfileDrawer } from '@/components/agents/AgentProfileDrawer';

interface Agent {
  _id: string;
  name: string;
  role: 'Rookie' | 'Senior' | 'External';
  description: string;
  status: 'active' | 'paused' | 'not-hired';
  sites?: string[];
  performanceData?: number[];
  performanceChange?: number;
  findingsCount?: number;
  createdAt?: string;
  lastActive?: string;
  price?: string;
  config?: Record<string, any>;
  metrics?: {
    responseTime?: number[];
    accuracy?: number[];
    successRate?: number[];
  };
  source?: 'PageMind' | 'Partner';
}

// NewAgent interface removed - agents are system assets

// Get API URL from localStorage if available, otherwise use the environment variable
const getApiUrl = () => {
  const savedApiUrl = localStorage.getItem('api_url');
  return savedApiUrl || import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
};

const API_URL = getApiUrl();

const AgentsPage: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const { trackOnboardingStep } = useOnboardingTracking();
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('name');
  const [activeTab, setActiveTab] = useState('all');
  
  // Set up axios auth header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Track "Meet the Crew" onboarding step when component mounts
  useEffect(() => {
    trackOnboardingStep('meet_crew', { timestamp: new Date().toISOString() });
  }, [trackOnboardingStep]);
  
  // New agent state removed - agents are system assets

  // Fetch agents (hardcoded list of system agents)
  const {
    data: agents,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    enabled: isAuthenticated,
    queryKey: ['agents'],
    queryFn: async () => {
      // Return hardcoded list of PageMind system agents
      return [
        {
          _id: 'seo-aeo-specialist',
          name: 'SEO/AEO Specialist',
          role: 'Senior' as const,
          description: 'Expert SEO and Answer Engine Optimization advisor. Analyzes SERP opportunities and provides actionable content recommendations to capture featured snippets, answer boxes, and improve rankings.',
          status: 'active' as const,
          performanceData: [85, 87, 89, 91, 93],
          performanceChange: 8.5,
          findingsCount: 0,
          createdAt: new Date().toISOString(),
          lastActive: new Date().toISOString()
        },
        {
          _id: 'page-chatbot',
          name: 'Page ChatBot',
          role: 'Senior' as const,
          description: 'Conversational AI that engages with website visitors, answers questions, and provides personalized assistance based on page content.',
          status: 'active' as const,
          performanceData: [78, 82, 85, 88, 90],
          performanceChange: 12.0,
          findingsCount: 0
        },
        {
          _id: 'site-crawler',
          name: 'Site Crawler',
          role: 'Senior' as const,
          description: 'Web scraping specialist that discovers and indexes website content, analyzes structure, and maintains up-to-date site data.',
          status: 'active' as const,
          performanceData: [92, 91, 93, 94, 95],
          performanceChange: 3.2,
          findingsCount: 0
        },
        {
          _id: 'voice-assistant',
          name: 'Voice Assistant',
          role: 'Senior' as const,
          description: 'Real-time voice interaction agent powered by Deepgram and ElevenLabs, providing natural voice-based customer support.',
          status: 'active' as const,
          performanceData: [70, 75, 80, 82, 85],
          performanceChange: 15.0,
          findingsCount: 0
        },
        {
          _id: 'customer-intent',
          name: 'Customer Intent Analyzer',
          role: 'Senior' as const,
          description: 'Analyzes user behavior patterns to understand purchase intent, predict actions, and provide personalized recommendations.',
          status: 'active' as const,
          performanceData: [80, 82, 84, 86, 88],
          performanceChange: 8.0,
          findingsCount: 0
        },
        {
          _id: 'page-semantic-intent',
          name: 'Page Semantics Learner',
          role: 'Senior' as const,
          description: 'Understands the semantic meaning and structure of page content, providing insights for SEO and content optimization.',
          status: 'active' as const,
          performanceData: [85, 86, 87, 89, 90],
          performanceChange: 5.5,
          findingsCount: 0
        }
      ];
    }
  });

  // Create agent mutation removed - agents are system assets
  
  // Update agent status mutation
  const updateAgentStatusMutation = useMutation({
    mutationFn: async ({ agentId, status }: { agentId: string, status: string }) => {
      const response = await axios.patch(`${API_URL}/agents/${agentId}/status`, { status });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      toast({
        title: "Success",
        description: "Agent status updated",
        variant: "default",
      });
    },
    onError: (error) => {
      console.error('Error updating agent status:', error);
      toast({
        title: "Error",
        description: "Failed to update agent status",
        variant: "destructive",
      });
    }
  });
  
  // Promote agent mutation
  const promoteAgentMutation = useMutation({
    mutationFn: async (agentId: string) => {
      const response = await axios.patch(`${API_URL}/agents/${agentId}/promote`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      toast({
        title: "Success",
        description: "Agent promoted to Senior",
        variant: "default",
      });
    },
    onError: (error) => {
      console.error('Error promoting agent:', error);
      toast({
        title: "Error",
        description: "Failed to promote agent",
        variant: "destructive",
      });
    }
  });
  
  const handlePauseAgent = (agent: Agent) => {
    const newStatus = agent.status === 'paused' ? 'active' : 'paused';
    updateAgentStatusMutation.mutate({ agentId: agent._id, status: newStatus });
  };

  const handlePromoteAgent = (agentId: string) => {
    promoteAgentMutation.mutate(agentId);
  };

  // Delete agent functionality removed - agents are system assets that users shouldn't delete

  const handleViewProfile = (agent: Agent) => {
    setSelectedAgent(agent);
    setIsProfileDrawerOpen(true);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (filter: string, value: string | null) => {
    if (filter === 'role') {
      setRoleFilter(value);
    } else if (filter === 'status') {
      setStatusFilter(value);
    }
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
  };

  // Sort agents based on current sortBy
  const sortAgents = (agents: Agent[]) => {
    return [...agents].sort((a: Agent, b: Agent) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'performance') {
        const aPerf = a.performanceData?.[a.performanceData.length - 1] || 0;
        const bPerf = b.performanceData?.[b.performanceData.length - 1] || 0;
        return bPerf - aPerf;
      } else if (sortBy === 'findings') {
        return (b.findingsCount || 0) - (a.findingsCount || 0);
      }
      return 0;
    });
  };

  // Count agents by status
  const activeAgentsCount = agents?.filter((a: Agent) => a.status === 'active').length || 0;
  const pausedAgentsCount = agents?.filter((a: Agent) => a.status === 'paused').length || 0;
  const needsInputCount = 0; // This will be calculated differently since we removed warning/error statuses

  // Log errors for debugging
  useEffect(() => {
    if (error) {
      console.error('Error fetching agents:', error);
    }
  }, [error]);

  // Handle hiring an agent from marketplace
  const handleHireAgent = (marketplaceAgent: Agent) => {
    // In a real implementation, this would call an API to add the agent to the user's account
    // Here we'll simulate the API call by updating our local state
    
    // Create a copy of the agent with status changed to 'active'
    const hiredAgent = {
      ...marketplaceAgent,
      status: 'active' as const,
      _id: `hired-${marketplaceAgent._id}` // Generate a new ID for the hired agent
    };
    
    // Add the hired agent to the agents list
    if (agents) {
      queryClient.setQueryData(['agents'], [...agents, hiredAgent]);
    }
    
    // Remove the agent from marketplace options by filtering it out
    const updatedMarketplaceAgents = marketplaceAgents.filter(agent => agent._id !== marketplaceAgent._id);
    setMarketplaceAgents(updatedMarketplaceAgents);
    
    // Show success message
    toast({
      title: "Agent hired successfully",
      description: `${marketplaceAgent.name} has been added to your team and is now active.`,
    });
    
    // Switch to the 'all' tab to show the newly hired agent
    setActiveTab('all');
  };

  // Define marketplace agents for the Marketplace tab (emptied)
  const [marketplaceAgents, setMarketplaceAgents] = useState<Agent[]>([]);

  // Apply filters and tab selection to get filtered agents
  const getFilteredAgents = () => {
    if (!agents) return [];
    
    // Start with system agents (not marketplace agents)
    let filtered = [...agents].filter(agent => agent.status !== 'not-hired');
    
    // Apply tab filtering
    if (activeTab === 'active') {
      filtered = filtered.filter((agent: Agent) => agent.status === 'active');
    } else if (activeTab === 'needsReview') {
      // For now, we're not using this tab since we removed warning/error statuses
      // This could be repurposed for agents that need attention based on other metrics
      filtered = [];
    } else if (activeTab === 'paused') {
      filtered = filtered.filter((agent: Agent) => agent.status === 'paused');
    } else if (activeTab === 'marketplace') {
      // For marketplace tab, we'll use the marketplace agents instead
      return [];
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((agent: Agent) => 
        agent.name.toLowerCase().includes(query) || 
        agent.description.toLowerCase().includes(query)
      );
    }
    
    // Apply role filter
    if (roleFilter) {
      filtered = filtered.filter((agent: Agent) => agent.role === roleFilter);
    }
    
    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter((agent: Agent) => agent.status === statusFilter);
    }
    
    return filtered;
  };

  // Get filtered and sorted agents
  const filteredAgents = sortAgents(getFilteredAgents());

  return (
    <div className="container mx-auto py-6">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Agents</h1>
          <div className="text-xs text-muted-foreground mt-1">
            API: <code className="bg-muted px-1 py-0.5 rounded">{API_URL}</code>
            <Link to="/agents/fix-agents" className="ml-2 text-primary hover:underline">
              <Wrench className="h-3 w-3 inline" /> Change
            </Link>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => refetch()} 
            disabled={isLoading || authLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
          <Button 
            onClick={() => setActiveTab('marketplace')}
            variant="default"
          >
            Hire Agent
          </Button>
        </div>
      </div>

      {authLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Authenticating...</span>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : isError ? (
        <div className="text-center p-4 border border-red-200 rounded-md bg-red-50">
          <div className="text-red-600 font-semibold mb-2">Error loading agents.</div>
          <p className="text-sm mb-4">This could be due to an API connection issue or authentication problem.</p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Button 
              onClick={() => refetch()} 
              variant="outline"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
            
            <Link to="/agents/fix-agents">
              <Button variant="default">
                <Wrench className="h-4 w-4 mr-2" />
                Troubleshoot Connection
              </Button>
            </Link>
          </div>
          
          <div className="mt-4 text-xs text-muted-foreground">
            <p>API URL: <code className="bg-muted px-1 py-0.5 rounded">{API_URL}</code></p>
            <p className="mt-1">Browser Console may have more details</p>
          </div>
        </div>
      ) : agents?.length === 0 ? (
        <EmptyStateNoAgents />
      ) : (
        <>
          {/* Filters */}
          {!isLoading && !isError && !authLoading && agents?.length > 0 && (
            <AgentFilters
              onSearch={handleSearch}
              onFilterChange={handleFilterChange}
              onSortChange={handleSortChange}
              activeAgentsCount={activeAgentsCount}
              pausedAgentsCount={pausedAgentsCount}
              needsInputCount={needsInputCount}
            />
          )}
          
          <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="mt-6">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="needsReview">Needs Review</TabsTrigger>
              <TabsTrigger value="paused">Paused</TabsTrigger>
              <TabsTrigger value="marketplace">Marketplace</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all">
              {filteredAgents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredAgents.map((agent: Agent) => (
                    <AgentCard
                      key={agent._id}
                      name={agent.name}
                      role={agent.role}
                      description={agent.description}
                      status={agent.status}
                      performanceData={agent.performanceData}
                      performanceChange={agent.performanceChange}
                      findingsCount={agent.findingsCount}
                      onViewProfile={() => handleViewProfile(agent)}
                      onPause={() => handlePauseAgent(agent)}
                      onPromote={() => handlePromoteAgent(agent._id)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyStateFiltered />
              )}
            </TabsContent>
            
            <TabsContent value="active">
              {filteredAgents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredAgents.map((agent: Agent) => (
                    <AgentCard
                      key={agent._id}
                      name={agent.name}
                      role={agent.role}
                      description={agent.description}
                      status={agent.status}
                      performanceData={agent.performanceData}
                      performanceChange={agent.performanceChange}
                      findingsCount={agent.findingsCount}
                      onViewProfile={() => handleViewProfile(agent)}
                      onPause={() => handlePauseAgent(agent)}
                      onPromote={() => handlePromoteAgent(agent._id)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyStateFiltered />
              )}
            </TabsContent>
            
            <TabsContent value="needsReview">
              {filteredAgents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredAgents.map((agent: Agent) => (
                    <AgentCard
                      key={agent._id}
                      name={agent.name}
                      role={agent.role}
                      description={agent.description}
                      status={agent.status}
                      performanceData={agent.performanceData}
                      performanceChange={agent.performanceChange}
                      findingsCount={agent.findingsCount}
                      onViewProfile={() => handleViewProfile(agent)}
                      onPause={() => handlePauseAgent(agent)}
                      onPromote={() => handlePromoteAgent(agent._id)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyStateFiltered />
              )}
            </TabsContent>
            
            <TabsContent value="paused">
              {filteredAgents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredAgents.map((agent: Agent) => (
                    <AgentCard
                      key={agent._id}
                      name={agent.name}
                      role={agent.role}
                      description={agent.description}
                      status={agent.status}
                      performanceData={agent.performanceData}
                      performanceChange={agent.performanceChange}
                      findingsCount={agent.findingsCount}
                      onViewProfile={() => handleViewProfile(agent)}
                      onPause={() => handlePauseAgent(agent)}
                      onPromote={() => handlePromoteAgent(agent._id)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyStateFiltered />
              )}
            </TabsContent>
            
            <TabsContent value="marketplace">
              <div className="mb-6 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <Package className="h-5 w-5" /> 
                    Agent Marketplace
                  </h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    Discover and hire specialized agents for your website optimization needs
                  </p>
                </div>
              </div>
              
              {marketplaceAgents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {marketplaceAgents.map((agent) => (
                    <AgentCard
                      key={agent._id}
                      name={agent.name}
                      role={agent.role}
                      description={agent.description}
                      status={agent.status}
                      performanceData={agent.performanceData}
                      performanceChange={agent.performanceChange}
                      findingsCount={agent.findingsCount}
                      price={agent.price}
                      source={agent.source}
                      onViewProfile={() => {}}
                      onHire={() => handleHireAgent(agent)}
                    />
                  ))}
                </div>
              ) : (
                <Card className="p-6 text-center">
                  <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <Ghost className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Marketplace agents have been hidden</h3>
                  <p className="text-muted-foreground text-sm">
                    As requested, all marketplace agents have been removed.
                  </p>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
      
      {/* Agent Profile Drawer */}
      <AgentProfileDrawer
        agent={selectedAgent}
        isOpen={isProfileDrawerOpen}
        onClose={() => setIsProfileDrawerOpen(false)}
        onPause={selectedAgent ? () => handlePauseAgent(selectedAgent) : undefined}
        onPromote={selectedAgent?.role === 'Rookie' ? () => handlePromoteAgent(selectedAgent._id) : undefined}
        // Delete functionality removed - agents are system assets
      />
    </div>
  );
};

// Empty state component when no agents have been found
const EmptyStateNoAgents = () => (
  <Card className="p-8 text-center">
    <div className="mx-auto mb-4 w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
      <Ghost className="h-10 w-10 text-primary" />
    </div>
    <h3 className="text-2xl font-bold mb-3">Agents have been hidden</h3>
    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
      As requested, all agents have been hidden from the dashboard.
    </p>
    <p className="text-sm text-muted-foreground">
      To restore agents, the code needs to be modified to fetch agents from the API again.
    </p>
  </Card>
);

// Empty state when filters return no results
const EmptyStateFiltered = () => (
  <Card className="p-6 text-center">
    <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-muted flex items-center justify-center">
      <Ghost className="h-6 w-6 text-muted-foreground" />
    </div>
    <h3 className="text-lg font-medium mb-2">No agents match these filters</h3>
    <p className="text-muted-foreground text-sm">
      Try adjusting your search or filter criteria
    </p>
  </Card>
);

export default AgentsPage;
